"use client";

import { TIndustriesConfig } from "@/app/config";
import { motion } from "motion/react";
import React from "react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.6
    }
  }
};

export default function IndustryGiants({config}:{config: TIndustriesConfig['IndustryGiants']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-white"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Company Cards Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
        >
          {config.companyData.map((company, index) => (
            <motion.div
              key={index}
              className={`${company.bgColor} ${company.borderColor} border rounded-xl p-6 cursor-pointer group`}
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 50,
                  scale: 0.9
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.6,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={{
                y: -8,
                scale: 1.03,
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              whileTap={{ scale: 0.97 }}
              style={{ 
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
              }}
            >
              {/* Company Logo and Info */}
              <motion.div 
                className="flex items-center gap-3 mb-6"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <motion.div 
                  className={`w-10 h-10 ${company.logoColor} rounded-lg flex items-center justify-center shadow-sm`}
                  variants={{
                    hidden: { 
                      opacity: 0, 
                      scale: 0.5,
                      rotate: -180
                    },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      rotate: 0,
                      transition: {
                        duration: 0.6,
                        ease: "backOut"
                      }
                    }
                  }}
                  whileHover={{
                    scale: 1.1,
                    rotate: 5,
                    transition: {
                      duration: 0.3,
                      ease: "easeOut"
                    }
                  }}
                  whileTap={{ scale: 0.9 }}
                >
                  <motion.span 
                    className={`font-bold text-lg ${company.logoTextColor}`}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 + 0.3, duration: 0.4 }}
                  >
                    {company.logoLetter}
                  </motion.span>
                </motion.div>
                <motion.div
                  variants={{
                    hidden: { opacity: 0, x: -20 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut"
                      }
                    }
                  }}
                >
                  <motion.h3 
                    className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 + 0.2, duration: 0.5 }}
                  >
                    {company.name}
                  </motion.h3>
                  <motion.p 
                    className="text-sm text-gray-600"
                    initial={{ opacity: 0, y: 5 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 + 0.3, duration: 0.5 }}
                  >
                    {company.subtitle}
                  </motion.p>
                </motion.div>
              </motion.div>

              {/* Financial Metrics */}
              <motion.div 
                className="space-y-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: index * 0.05 + 0.4
                    }
                  }
                }}
              >
                {/* Revenue */}
                <motion.div 
                  className="flex justify-between items-center group/metric"
                  variants={ {
                    hidden: { opacity: 0, x: 20 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.4,
                        ease: "easeOut"
                      }
                    }
                  }}
                  whileHover={{ 
                    x: 5,
                    transition: { duration: 0.2 }
                  }}
                >
                  <motion.span 
                    className="text-sm text-gray-700 font-medium"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 + 0.5, duration: 0.4 }}
                  >
                    Revenue
                  </motion.span>
                  <motion.span 
                    className="text-lg font-bold text-gray-900 group-hover/metric:text-green-600 transition-colors duration-300"
                    variants={{
                      hidden: { 
                        opacity: 0, 
                        scale: 0.8,
                        y: 10
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "backOut"
                        }
                      }
                    }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {company.revenue}
                  </motion.span>
                </motion.div>

                {/* Market Cap */}
                <motion.div 
                  className="flex justify-between items-center group/metric"
                  variants={ {
                    hidden: { opacity: 0, x: 20 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.4,
                        ease: "easeOut"
                      }
                    }
                  }}
                  whileHover={{ 
                    x: 5,
                    transition: { duration: 0.2 }
                  }}
                >
                  <motion.span 
                    className="text-sm text-gray-700 font-medium"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 + 0.6, duration: 0.4 }}
                  >
                    Market Cap
                  </motion.span>
                  <motion.span 
                    className="text-lg font-bold text-gray-900 group-hover/metric:text-blue-600 transition-colors duration-300"
                    variants={{
                      hidden: { 
                        opacity: 0, 
                        scale: 0.8,
                        y: 10
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: "backOut"
                        }
                      }
                    }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {company.marketCap}
                  </motion.span>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}